/**
 *
 * PreviewPageForm
 *
 */

import React from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { usePreviewPagesSlice } from './slice';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectPreviewPage,
  selectSavePreviewPage,
  selectUpdatePeople,
} from './slice/selectors';
import { useSnackbar } from 'notistack';
import { PreviewPageForm } from './Form';
import { People } from './People';
import { ChevronLeft } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { Helmet } from 'react-helmet-async';

export function Edit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { actions } = usePreviewPagesSlice();
  const loadPreviewPage = useSelector(selectPreviewPage);
  const savePreviewPage = useSelector(selectSavePreviewPage);
  const updatePeople = useSelector(selectUpdatePeople);

  const { enqueueSnackbar } = useSnackbar();

  const params = useParams<{
    id: string;
    section?: string;
  }>();

  const basePath = '/portal/preview-pages';

  // load()/save-response effects are intentionally keyed to mount / route id /
  // request-loading transitions. load() and dispatch/actions are recreated or
  // stable; adding them (or useCallback-ing load) re-runs the loads on every
  // render. Documented disables keep the original load triggers exactly.
  React.useEffect(() => {
    load();
    return () => {
      dispatch(actions.loadPreviewPageUnmount());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (loadPreviewPage.error) {
      navigate('/portal/preview-pages');
    }
  }, [loadPreviewPage, navigate]);

  React.useEffect(() => {
    if (savePreviewPage.loading) return;
    if (savePreviewPage.error) {
      enqueueSnackbar(savePreviewPage.error.message, {
        variant: 'error',
      });
    }
    if (savePreviewPage.data) {
      enqueueSnackbar('Preview page saved successfully', {
        variant: 'success',
      });
      if (params.id === 'new') {
        navigate(`/portal/preview-pages/${savePreviewPage.data.id}`, {
          replace: true,
        });
      } else {
        load();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savePreviewPage.loading]);

  React.useEffect(() => {
    if (params.id !== 'new') load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  React.useEffect(() => {
    if (!updatePeople.loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePeople.loading]);

  const load = () => {
    const id = params.id === 'new' ? 'new' : parseInt(params.id || '');
    dispatch(actions.loadPreviewPageRequest({ id }));
  };

  return (
    <>
      <Helmet>
        <title>
          {loadPreviewPage.data ? loadPreviewPage.data.reference : 'Create new'}
          - Preview Page
        </title>
      </Helmet>
      <Box>
        <Paper
          square
          sx={{
            backgroundColor: 'secondary.dark',
            color: 'white',
            py: 1,
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ flex: '0 0 20%', pl: 1 }}>
            <Button
              color={'inherit'}
              size={'small'}
              variant="text"
              startIcon={<ChevronLeft />}
              sx={{ marginLeft: 'auto' }}
              component={Link}
              to={basePath}
            >
              Back to preview pages
            </Button>
          </Box>

          <Box sx={{ flex: '0 0 20%', textAlign: 'right', pr: 1 }} />
        </Paper>
        <Container sx={{ my: 4 }} maxWidth={'xl'}>
          <Paper>
            <Box
              sx={{
                px: 1,
                backgroundColor: grey['200'],
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Tabs
                value={params.section || ''}
                sx={{ flexGrow: 1, minWidth: 0 }}
              >
                <Tab
                  value={''}
                  component={Link}
                  to={`${basePath}/${params.id}`}
                  label={'General'}
                />
                <Tab
                  value={'people'}
                  disabled={params.id === 'new'}
                  component={Link}
                  to={`${basePath}/${params.id}/people`}
                  label={'People'}
                />
              </Tabs>
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                {!!loadPreviewPage.data &&
                  (() => {
                    const page = loadPreviewPage.data!;
                    const servable = page.active && !page.expired;
                    const reason = !page.active
                      ? 'This page is inactive and cannot be viewed publicly.'
                      : page.expired
                      ? 'This page has expired and cannot be viewed publicly.'
                      : '';
                    const button = (
                      <span>
                        <Button
                          variant={'contained'}
                          size={'small'}
                          color={'warning'}
                          disabled={!servable}
                          onClick={() => window.open(page.public_url, '_blank')}
                        >
                          View public page
                        </Button>
                      </span>
                    );
                    return servable ? (
                      button
                    ) : (
                      <Tooltip title={reason}>{button}</Tooltip>
                    );
                  })()}
              </Box>
            </Box>

            {!params.section && (
              <PreviewPageForm
                loadPreviewPage={loadPreviewPage}
                savePreviewPage={savePreviewPage}
                onSave={data => dispatch(actions.savePreviewPageRequest(data))}
              />
            )}

            {params.section === 'people' && (
              <People
                updatePeople={updatePeople}
                loadPreviewPage={loadPreviewPage}
                onUpdate={data =>
                  dispatch(
                    actions.updatePeopleRequest({
                      ...data,
                      id: loadPreviewPage.data?.id,
                    }),
                  )
                }
              />
            )}
          </Paper>
        </Container>
      </Box>
    </>
  );
}
